import type { FormKitNode } from '@formkit/core'
import { de, es, fr, ja } from '@formkit/i18n'
import type { FormKitBaseSlots, FormKitInputs, FormKitSlotData } from '@formkit/inputs'
import { createProPlugin, inputs } from '@formkit/pro'
import { createInput, defaultConfig } from '@formkit/vue'
import { rootClasses } from '../osso-formkit-theme-override'
import FormCheckbox from './components/forms/FormCheckbox.vue'
import FormDateRangeInput from './components/forms/FormDateRangeInput.vue'
import FormInput from './components/forms/FormInput.vue'
import FormMultiSelect from './components/forms/FormMultiSelect.vue'
import FormPlatformSelect from './components/forms/FormPlatformSelect.vue'
import FormRadioButtons from './components/forms/FormRadioButtons.vue'
import FormResult from './components/forms/FormResult.vue'
import FormSelect from './components/forms/FormSelect.vue'
import FormSubmit from './components/forms/FormSubmit.vue'
import FormToggle from './components/forms/FormToggle.vue'
import FormUserSelect from './components/forms/FormUserSelect.vue'
import type { IconName } from './components/icons/material-icon'
import type { SelectProps } from './components/primevue/select'

declare module '@formkit/inputs' {
  interface FormKitInputSlots<Props extends FormKitInputs<Props>> {
    ossoMultiSelect: FormKitBaseSlots<Props> & {
      option: FormKitSlotData<Props, { option: { label: string; value: string } }>
    }
  }

  interface FormKitInputProps<Props extends FormKitInputs<Props>> {
    ossoDateRange: {
      type: 'ossoDateRange'
    }
    ossoInput: {
      type: 'ossoInput'
    }
    ossoRadioButtons: {
      type: 'ossoRadioButtons'
    }
    ossoCheckbox: {
      type: 'ossoCheckbox'
    }
    ossoResult: {
      type: 'ossoResult'
    }
    ossoSelect: {
      type: 'ossoSelect'
      options?: SelectProps['options']
      loadOptions?: SelectProps['loadOptions']
      ensureSelected?: SelectProps['ensureSelected']
    }
    ossoSubmit: {
      type: 'ossoSubmit'
      icon?: IconName
    }
    ossoUserSelect: {
      type: 'ossoUserSelect'
    }
    ossoPlatformSelect: {
      type: 'ossoUserSelect'
    }
    ossoToggle: {
      type: 'ossoToggle'
    }
    ossoMultiSelect: {
      type: 'ossoMultiSelect'
      options?: SelectProps['options']
      loadOptions?: SelectProps['loadOptions']
      ensureSelected?: SelectProps['ensureSelected']
    }
  }
}

const proPlugin = createProPlugin('fk-b7f284e407', inputs)

function setNodePropsPlugin(node: FormKitNode) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  node.props.attrs.placeholder = node.props.label
  if (node.props.type === 'ossoResult') {
    node.props.id = 'result'
    node.props.ignore = true
  }
}

function disableSubmitAndIncompleteMessagePlugin(node: FormKitNode) {
  node.props.actions = false // disable submit
  node.props.incompleteMessage = false // disable default incomplete message above submit
}

function dataTestIdPlugin(node: FormKitNode) {
  node.on('created', () => {
    if (!node.props.definition || typeof node.props.definition.schema !== 'function') {
      return
    }
    node.props.definition.schemaMemoKey = `data-testid_${node.props.definition.schemaMemoKey}`

    const schemaFn = node.props.definition.schema
    node.props.definition.schema = (sectionsSchema = {}) => {
      sectionsSchema.outer = {
        attrs: {
          'data-testid': '$: "control-" + $node.name'
        }
      }
      return schemaFn(sectionsSchema)
    }
  })
}

export const config = defaultConfig({
  config: {
    rootClasses
  },
  plugins: [proPlugin, setNodePropsPlugin, dataTestIdPlugin, disableSubmitAndIncompleteMessagePlugin],
  locales: { de, fr, es, ja },
  inputs: {
    ossoDateRange: createInput(FormDateRangeInput),
    ossoInput: createInput(FormInput),
    ossoMultiSelect: createInput(FormMultiSelect, { props: ['options', 'loadOptions', 'ensureSelected'] }),
    ossoRadioButtons: createInput(FormRadioButtons),
    ossoCheckbox: createInput(FormCheckbox),
    ossoResult: createInput(FormResult),
    ossoSelect: createInput(FormSelect, { props: ['options', 'loadOptions', 'ensureSelected'] }),
    ossoSubmit: createInput(FormSubmit, { props: ['icon'] }),
    ossoUserSelect: createInput(FormUserSelect),
    ossoPlatformSelect: createInput(FormPlatformSelect),
    ossoToggle: createInput(FormToggle)
  }
})
